import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { dashboardApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Car,
  Users,
  FileText,
  Money,
  TrendUp,
  TrendDown,
  Warning,
  Bank,
  ArrowRight,
  ChartLineUp,
  Percent,
  X,
  Phone,
  MagnifyingGlass
} from '@phosphor-icons/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend
} from 'recharts';

const Dashboard = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [monthlyFlow, setMonthlyFlow] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showOverdueDetail, setShowOverdueDetail] = useState(false);
  const [overdueDetail, setOverdueDetail] = useState(null);
  const [overdueLoading, setOverdueLoading] = useState(false);
  const [overdueSearch, setOverdueSearch] = useState('');
  const [closingInactive, setClosingInactive] = useState(false);
  const isAdmin = user?.role === 'admin';

  const handleCloseInactiveSchedules = async () => {
    if (!overdueDetail) return;
    const inactiveCount = overdueDetail.items.filter(it => it.contract_status && it.contract_status !== 'activo').length;
    if (inactiveCount === 0) {
      setError('No hay cuotas de contratos inactivos para cerrar.');
      return;
    }
    if (!window.confirm(
      `¿Cerrar ${inactiveCount} cuotas pendientes de contratos cancelados/liquidados?\n\n` +
      `Las cuotas se marcarán como "cancelado" y desaparecerán de la cartera vencida.\n` +
      `Esta acción NO afecta los contratos activos.`
    )) return;
    try {
      setClosingInactive(true);
      const API = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.post(`${API}/api/dashboard/close-inactive-overdue-schedules`);
      // Recargar todo
      setOverdueDetail(null);
      await loadDashboardData();
      // Reabrir el modal con datos frescos
      const reload = await axios.get(`${API}/api/dashboard/overdue-portfolio-detail`);
      setOverdueDetail(reload.data);
      alert(`✅ ${data.closed} cuotas cerradas. La cartera vencida ahora refleja solo contratos activos.`);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setClosingInactive(false);
    }
  };

  const openOverdueDetail = async () => {
    setShowOverdueDetail(true);
    if (overdueDetail) return; // ya cargado
    try {
      setOverdueLoading(true);
      const API = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API}/api/dashboard/overdue-portfolio-detail`);
      setOverdueDetail(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setOverdueLoading(false);
    }
  };

  const overdueFiltered = useMemo(() => {
    if (!overdueDetail) return [];
    const q = overdueSearch.trim().toLowerCase();
    if (!q) return overdueDetail.items;
    return overdueDetail.items.filter(
      (it) =>
        (it.client_name || '').toLowerCase().includes(q) ||
        (it.vehicle_plates || '').toLowerCase().includes(q) ||
        (it.vehicle_label || '').toLowerCase().includes(q) ||
        (it.client_phone || '').includes(q)
    );
  }, [overdueDetail, overdueSearch]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [summaryRes, kpisRes, alertsRes, flowRes] = await Promise.all([
        dashboardApi.getSummary(),
        dashboardApi.getKPIs(),
        dashboardApi.getAlerts(),
        dashboardApi.getMonthlyFlow()
      ]);
      setSummary(summaryRes.data);
      setKpis(kpisRes.data);
      setAlerts(alertsRes.data);
      setMonthlyFlow(flowRes.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatPercent = (value) => `${(value || 0).toFixed(2)}%`;

  const chartTick = useMemo(() => ({ fontSize: 12 }), []);
  const chartRadius = useMemo(() => [4, 4, 0, 0], []);
  const tooltipStyle = useMemo(() => ({
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: '8px'
  }), []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="dashboard-loading">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" data-testid="dashboard-error">
        <Warning size={20} />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-[#0F172A]">Dashboard</h1>
          <p className="text-[#64748B]">Bienvenido, {user?.name}</p>
        </div>
        <button
          onClick={loadDashboardData}
          className="btn-secondary flex items-center gap-2"
          data-testid="refresh-dashboard-btn"
        >
          <ChartLineUp size={18} />
          Actualizar
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {/* Vehicles */}
        <div className="kpi-card" data-testid="kpi-vehicles">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Vehículos Rentados</p>
              <p className="kpi-value">{summary?.vehicles?.rented || 0}</p>
              <p className="text-sm text-[#64748B] mt-1">
                de {summary?.vehicles?.total || 0} total
              </p>
            </div>
            <div className="w-12 h-12 bg-[#DBEAFE] rounded-lg flex items-center justify-center">
              <Car size={24} className="text-[#0055FF]" />
            </div>
          </div>
        </div>

        {/* Clients */}
        <div className="kpi-card" data-testid="kpi-clients">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Clientes Activos</p>
              <p className="kpi-value">{summary?.clients?.active || 0}</p>
              <p className="text-sm text-[#64748B] mt-1">
                de {summary?.clients?.total || 0} total
              </p>
            </div>
            <div className="w-12 h-12 bg-[#D1FAE5] rounded-lg flex items-center justify-center">
              <Users size={24} className="text-[#10B981]" />
            </div>
          </div>
        </div>

        {/* Portfolio */}
        <div className="kpi-card" data-testid="kpi-portfolio">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Cartera Total</p>
              <p className="kpi-value text-2xl">{formatCurrency(summary?.portfolio?.total)}</p>
              <p className="text-sm text-[#64748B] mt-1">
                {summary?.contracts?.active || 0} contratos activos
              </p>
            </div>
            <div className="w-12 h-12 bg-[#FEF3C7] rounded-lg flex items-center justify-center">
              <FileText size={24} className="text-[#F59E0B]" />
            </div>
          </div>
        </div>

        {/* Deuda Total Acreedores */}
        <div className="kpi-card" data-testid="kpi-debt">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Deuda Acreedores</p>
              <p className="kpi-value text-2xl text-[#E11D48]">{formatCurrency(summary?.creditors?.total_debt)}</p>
              <p className="text-sm text-[#64748B] mt-1">
                Pago: {formatCurrency(summary?.creditors?.monthly_payments)}/mes
              </p>
            </div>
            <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
              <Bank size={24} className="text-[#E11D48]" />
            </div>
          </div>
        </div>

        {/* Weekly Flow */}
        <div className="kpi-card" data-testid="kpi-weekly-flow">
          <div className="flex items-start justify-between">
            <div>
              <p className="kpi-label">Cobranza esta semana</p>
              <p className="kpi-value text-2xl">{formatCurrency(summary?.weekly_flow?.received)}</p>
              <p className="text-sm text-[#64748B] mt-1">
                de {formatCurrency(summary?.weekly_flow?.expected)} esperado
              </p>
              <p className="text-xs text-[#94A3B8] mt-0.5">
                Renta + accesorios cobrados (lun–dom)
              </p>
            </div>
            <div className="w-12 h-12 bg-[#ECFDF5] rounded-lg flex items-center justify-center">
              <Money size={24} className="text-[#10B981]" />
            </div>
          </div>
        </div>
      </div>

      {/* KPIs Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Risk Indicators */}
        <div className="card p-6" data-testid="risk-indicators">
          <h3 className="text-lg font-heading font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <Warning size={20} className="text-[#E11D48]" />
            Indicadores de Riesgo
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-[#F8FAFC] rounded-lg">
              <p className="text-xs uppercase tracking-wider text-[#64748B] mb-1">PAR 30</p>
              <p className={`text-2xl font-bold ${(kpis?.risk_indicators?.par30 || 0) > 5 ? 'text-[#E11D48]' : 'text-[#0F172A]'}`}>
                {formatPercent(kpis?.risk_indicators?.par30)}
              </p>
            </div>
            <div className="p-4 bg-[#F8FAFC] rounded-lg">
              <p className="text-xs uppercase tracking-wider text-[#64748B] mb-1">PAR 60</p>
              <p className={`text-2xl font-bold ${(kpis?.risk_indicators?.par60 || 0) > 3 ? 'text-[#E11D48]' : 'text-[#0F172A]'}`}>
                {formatPercent(kpis?.risk_indicators?.par60)}
              </p>
            </div>
            <div className="p-4 bg-[#F8FAFC] rounded-lg">
              <p className="text-xs uppercase tracking-wider text-[#64748B] mb-1">PAR 90</p>
              <p className={`text-2xl font-bold ${(kpis?.risk_indicators?.par90 || 0) > 2 ? 'text-[#E11D48]' : 'text-[#0F172A]'}`}>
                {formatPercent(kpis?.risk_indicators?.par90)}
              </p>
            </div>
            <div className="p-4 bg-[#F8FAFC] rounded-lg">
              <p className="text-xs uppercase tracking-wider text-[#64748B] mb-1">NPL Ratio</p>
              <p className={`text-2xl font-bold ${(kpis?.risk_indicators?.npl_ratio || 0) > 5 ? 'text-[#E11D48]' : 'text-[#0F172A]'}`}>
                {formatPercent(kpis?.risk_indicators?.npl_ratio)}
              </p>
            </div>
          </div>
          <button
            onClick={openOverdueDetail}
            className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg w-full text-left hover:bg-red-100 hover:border-red-300 transition-colors"
            data-testid="open-overdue-detail-btn"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#E11D48] font-medium">
                Cartera Vencida: {formatCurrency(kpis?.risk_indicators?.overdue_amount)}
              </p>
              <span className="text-xs text-[#E11D48] font-medium underline">
                Ver desglose
              </span>
            </div>
          </button>
        </div>

        {/* Profitability Indicators */}
        <div className="card p-6" data-testid="profitability-indicators">
          <h3 className="text-lg font-heading font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <TrendUp size={20} className="text-[#10B981]" />
            Indicadores de Rentabilidad — Arrendadora
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 bg-[#F8FAFC] rounded-lg">
              <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Spread de Arrendamiento</p>
              <p className={`text-2xl font-bold ${(kpis?.profitability_indicators?.lease_spread || 0) >= 0 ? 'text-[#10B981]' : 'text-[#E11D48]'}`}>
                {formatPercent(kpis?.profitability_indicators?.lease_spread)}
              </p>
              <p className="text-[10px] text-[#94A3B8] mt-1">Tasa rentas - Costo fondeo</p>
            </div>
            <div className="p-4 bg-[#F8FAFC] rounded-lg">
              <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Margen Neto Arrendamiento</p>
              <p className={`text-2xl font-bold ${(kpis?.profitability_indicators?.net_lease_margin || 0) >= 0 ? 'text-[#10B981]' : 'text-[#E11D48]'}`}>
                {formatPercent(kpis?.profitability_indicators?.net_lease_margin)}
              </p>
              <p className="text-[10px] text-[#94A3B8] mt-1">(Rentas - Fondeo - Dep - Seguros) / Activos</p>
            </div>
            <div className="p-4 bg-[#F8FAFC] rounded-lg">
              <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Yield Cartera Arrendada</p>
              <p className="text-2xl font-bold text-[#10B981]">
                {formatPercent(kpis?.profitability_indicators?.lease_yield)}
              </p>
              <p className="text-[10px] text-[#94A3B8] mt-1">Rentas anualizadas / Cartera</p>
            </div>
            <div className="p-4 bg-[#F8FAFC] rounded-lg">
              <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">DSCR (Cobertura Deuda)</p>
              <p className={`text-2xl font-bold ${(kpis?.profitability_indicators?.dscr || 0) >= 1.2 ? 'text-[#10B981]' : (kpis?.profitability_indicators?.dscr || 0) >= 1 ? 'text-[#F59E0B]' : 'text-[#E11D48]'}`}>
                {(kpis?.profitability_indicators?.dscr || 0).toFixed(2)}x
              </p>
              <p className="text-[10px] text-[#94A3B8] mt-1">Flujo rentas / Pagos acreedores</p>
            </div>
            <div className="p-4 bg-[#F8FAFC] rounded-lg">
              <p className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">Occupancy Rate</p>
              <p className="text-2xl font-bold text-[#0055FF]">
                {formatPercent(kpis?.profitability_indicators?.occupancy_rate)}
              </p>
              <p className="text-[10px] text-[#94A3B8] mt-1">Vehículos rentados / Total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts and Alerts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Flow Chart */}
        <div className="card p-6 lg:col-span-2" data-testid="monthly-flow-chart">
          <h3 className="text-lg font-heading font-bold text-[#0F172A] mb-4">
            Flujo Mensual
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyFlow}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={chartTick} stroke="#64748B" />
                <YAxis tick={chartTick} stroke="#64748B" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={tooltipStyle}
                />
                <Legend />
                <Bar dataKey="income" name="Ingresos" fill="#10B981" radius={chartRadius} />
                <Bar dataKey="expenses" name="Egresos" fill="#E11D48" radius={chartRadius} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Alerts */}
        <div className="card p-6" data-testid="alerts-panel">
          <h3 className="text-lg font-heading font-bold text-[#0F172A] mb-4 flex items-center gap-2">
            <Warning size={20} className="text-[#F59E0B]" />
            Alertas de Cobranza
          </h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {alerts.length === 0 ? (
              <p className="text-sm text-[#64748B] text-center py-8">
                Sin alertas pendientes
              </p>
            ) : (
              alerts.slice(0, 10).map((alert, index) => (
                <div
                  key={`${alert.client_id}-${alert.due_date}-${index}`}
                  className={`p-3 rounded-lg border ${
                    alert.severity === 'high'
                      ? 'bg-red-50 border-red-200'
                      : alert.severity === 'medium'
                      ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}
                  data-testid={`alert-item-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#0F172A]">
                        {alert.client_name}
                      </p>
                      <p className="text-xs text-[#64748B]">
                        {alert.days_late} días de atraso
                      </p>
                    </div>
                    <span className={`badge ${
                      alert.severity === 'high' ? 'badge-danger' : 
                      alert.severity === 'medium' ? 'badge-warning' : 'badge-info'
                    }`}>
                      {formatCurrency(alert.amount)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          {alerts.length > 10 && (
            <button className="w-full mt-4 text-sm text-[#0055FF] hover:underline flex items-center justify-center gap-1">
              Ver todas las alertas
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Creditor Summary */}
      <div className="card p-6" data-testid="creditor-summary">
        <h3 className="text-lg font-heading font-bold text-[#0F172A] mb-4 flex items-center gap-2">
          <Bank size={20} className="text-[#0055FF]" />
          Resumen de Acreedores
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-[#F8FAFC] rounded-lg">
            <p className="text-xs uppercase tracking-wider text-[#64748B] mb-1">Deuda Total</p>
            <p className="text-2xl font-bold text-[#0F172A]">
              {formatCurrency(summary?.creditors?.total_debt)}
            </p>
          </div>
          <div className="p-4 bg-[#F8FAFC] rounded-lg">
            <p className="text-xs uppercase tracking-wider text-[#64748B] mb-1">Pago Mensual</p>
            <p className="text-2xl font-bold text-[#0F172A]">
              {formatCurrency(summary?.creditors?.monthly_payments)}
            </p>
          </div>
          <div className="p-4 bg-[#F8FAFC] rounded-lg">
            <p className="text-xs uppercase tracking-wider text-[#64748B] mb-1">Acreedores</p>
            <p className="text-2xl font-bold text-[#0F172A]">
              {summary?.creditors?.count || 0}
            </p>
          </div>
        </div>
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-[#0055FF] font-medium">
            Ratio de Apalancamiento: {kpis?.portfolio_metrics?.leverage_ratio || 0}x
          </p>
        </div>
      </div>

      {/* Modal Cartera Vencida — desglose */}
      {showOverdueDetail && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="overdue-detail-modal"
        >
          <div className="bg-white rounded-xl shadow-xl w-[95vw] max-w-[1400px] max-h-[92vh] flex flex-col">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <h2 className="text-xl font-heading font-bold text-[#0F172A] flex items-center gap-2">
                <Warning size={22} className="text-[#E11D48]" />
                Cartera Vencida — Desglose (cuotas con ≥30 días de atraso)
              </h2>
              <button
                onClick={() => setShowOverdueDetail(false)}
                className="text-[#64748B] hover:text-[#0F172A]"
                aria-label="cerrar"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5">
              {overdueLoading && (
                <div className="flex items-center justify-center py-12">
                  <div className="spinner" />
                </div>
              )}

              {!overdueLoading && overdueDetail && (
                <>
                  {/* KPIs */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="kpi-card bg-red-50/50">
                      <p className="kpi-label">Total Vencido</p>
                      <p className="text-2xl font-bold text-[#E11D48]">{formatCurrency(overdueDetail.total_due)}</p>
                    </div>
                    <div className="kpi-card">
                      <p className="kpi-label">Renta atrasada</p>
                      <p className="text-xl font-bold text-[#0F172A]">{formatCurrency(overdueDetail.total_rent)}</p>
                    </div>
                    <div className="kpi-card">
                      <p className="kpi-label">Mora acumulada</p>
                      <p className="text-xl font-bold text-[#E11D48]">{formatCurrency(overdueDetail.total_penalty)}</p>
                    </div>
                    <div className="kpi-card">
                      <p className="kpi-label">Accesorios</p>
                      <p className="text-xl font-bold text-[#0055FF]">{formatCurrency(overdueDetail.total_accessory)}</p>
                    </div>
                    <div className="kpi-card">
                      <p className="kpi-label">Conductores</p>
                      <p className="text-2xl font-bold text-[#0055FF]">{overdueDetail.unique_drivers}</p>
                      <p className="text-xs text-[#94A3B8]">{overdueDetail.weeks_count} cuotas</p>
                    </div>
                  </div>

                  {/* Por rango de días */}
                  <div>
                    <h4 className="text-sm font-semibold text-[#0F172A] mb-2">Por rango de días de atraso</h4>
                    <div className="overflow-x-auto border border-[#E2E8F0] rounded-md">
                      <table className="data-table" data-testid="overdue-by-range">
                        <thead className="bg-[#F8FAFC]">
                          <tr>
                            <th>Rango días</th>
                            <th className="text-right">Conductores</th>
                            <th className="text-right">Cuotas</th>
                            <th className="text-right">Renta</th>
                            <th className="text-right">Mora</th>
                            <th className="text-right">Accesorios</th>
                            <th className="text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {overdueDetail.by_range.map((b) => (
                            <tr key={b.range}>
                              <td className="font-semibold">{b.range} días</td>
                              <td className="text-right">{b.drivers}</td>
                              <td className="text-right">{b.weeks}</td>
                              <td className="text-right">{formatCurrency(b.rent)}</td>
                              <td className="text-right text-[#E11D48]">{formatCurrency(b.penalty)}</td>
                              <td className="text-right text-[#0055FF]">{formatCurrency(b.accessory)}</td>
                              <td className="text-right font-bold text-[#E11D48]">{formatCurrency(b.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Search */}
                  <div className="relative">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Buscar por conductor, placa, vehículo o teléfono..."
                      value={overdueSearch}
                      onChange={(e) => setOverdueSearch(e.target.value)}
                      className="input pl-10 pr-10"
                      data-testid="overdue-modal-search"
                    />
                    {overdueSearch && (
                      <button
                        onClick={() => setOverdueSearch('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  {/* Detalle */}
                  <div>
                    <h4 className="text-sm font-semibold text-[#0F172A] mb-2 flex items-center justify-between">
                      <span>Detalle por cuota</span>
                      {overdueSearch && (
                        <span className="text-xs font-normal text-[#64748B]">
                          Filtrado: {overdueFiltered.length} de {overdueDetail.items.length}
                        </span>
                      )}
                    </h4>
                    <div className="overflow-x-auto border border-[#E2E8F0] rounded-md">
                      <table className="data-table text-xs" data-testid="overdue-detail-table">
                        <thead className="bg-[#F8FAFC]">
                          <tr>
                            <th className="sticky left-0 bg-[#F8FAFC] z-10 min-w-[180px]">Conductor</th>
                            <th>Teléfono</th>
                            <th>Vehículo</th>
                            <th>Semana</th>
                            <th>Vencimiento</th>
                            <th className="text-right">Días</th>
                            <th className="text-right">Renta</th>
                            <th className="text-right">Mora</th>
                            <th className="text-right">Accesorios</th>
                            <th className="text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {overdueFiltered.length === 0 ? (
                            <tr>
                              <td colSpan={10} className="text-center text-[#94A3B8] py-8">
                                Sin coincidencias
                              </td>
                            </tr>
                          ) : overdueFiltered.map((it) => (
                            <tr key={it.schedule_id} className={it.contract_status !== 'activo' ? 'bg-amber-50/30' : ''}>
                              <td className="sticky left-0 bg-white z-10 font-medium border-r border-[#E2E8F0]">
                                {it.client_name}
                                {it.contract_status && it.contract_status !== 'activo' && (
                                  <span className="ml-2 text-[10px] bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded uppercase font-semibold" title={`Contrato ${it.contract_status}`}>
                                    {it.contract_status}
                                  </span>
                                )}
                              </td>
                              <td>
                                {it.client_phone ? (
                                  <a href={`tel:${it.client_phone}`} className="flex items-center gap-1 hover:underline text-[#0055FF]">
                                    <Phone size={12} /> {it.client_phone}
                                  </a>
                                ) : '-'}
                              </td>
                              <td>{it.vehicle_label} <span className="text-[#94A3B8]">({it.vehicle_plates})</span></td>
                              <td>Sem {it.week_number}</td>
                              <td>{it.due_date}</td>
                              <td className="text-right font-bold text-[#E11D48]">{it.days_late}</td>
                              <td className="text-right">{formatCurrency(it.expected_amount)}</td>
                              <td className="text-right text-[#E11D48]">{formatCurrency(it.penalty_amount)}</td>
                              <td className="text-right text-[#0055FF]">{formatCurrency(it.accessory_amount)}</td>
                              <td className="text-right font-bold text-[#E11D48]">{formatCurrency(it.total_due)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-[#E2E8F0] flex items-center justify-between gap-2">
              <div>
                {isAdmin && overdueDetail && overdueDetail.items.some(it => it.contract_status && it.contract_status !== 'activo') && (
                  <button
                    onClick={handleCloseInactiveSchedules}
                    disabled={closingInactive}
                    className="btn-secondary text-[#E11D48] hover:bg-red-50 border-red-200 flex items-center gap-2 disabled:opacity-50"
                    data-testid="close-inactive-schedules-btn"
                  >
                    {closingInactive ? 'Cerrando...' : `Cerrar ${overdueDetail.items.filter(it => it.contract_status && it.contract_status !== 'activo').length} cuotas de contratos cancelados/liquidados`}
                  </button>
                )}
              </div>
              <button onClick={() => setShowOverdueDetail(false)} className="btn-secondary">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
